import React, { useState, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import {
  RefreshCw,
  Calendar,
  ChevronDown,
  AlertTriangle,
  Users,
  Wrench,
  Car,
  Layers,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCcw,
  Database,
} from 'lucide-react'
import styles from './FeedMonitor.module.scss'
import { config } from '../../config'

const MONITOR_API_URL = config.MONITOR_API_URL

type EntityType = 'all' | 'cars' | 'modifications' | 'models' | 'generations'
type FilterType = 'all' | 'MISSING_DEALER' | 'MISSING_MODIFICATION' | 'MISSING_MODEL' | 'API_ERROR' | 'queues'

interface StatusData {
  total: number
  by_status: Array<{ status: string; count: number }>
  by_index?: Array<{ index: string; count: number }>
  by_db_check_status?: Array<{ status: string; count: number }>
}

interface ErrorData {
  by_error_type: Array<{ error_type: string; count: number }>
}

interface MissingRef {
  ref_id: string
  count: number
}

interface QueueData {
  name: string
  messages: number
  consumers: number
}

interface IndexInfo {
  index: string
  docs_count: number
}

const ENTITY_TYPES: { value: EntityType; label: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'All Entities', icon: <Layers size={14} /> },
  { value: 'cars', label: 'Cars', icon: <Car size={14} /> },
  { value: 'modifications', label: 'Modifications', icon: <Wrench size={14} /> },
  { value: 'models', label: 'Models', icon: <Database size={14} /> },
  { value: 'generations', label: 'Generations', icon: <Layers size={14} /> },
]

const FeedMonitor: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [selectedEntity, setSelectedEntity] = useState<EntityType>('all')
  const [quickDate, setQuickDate] = useState<string>('today')
  const [activeFilter, setActiveFilter] = useState<FilterType>('all')
  const [stats, setStats] = useState<StatusData | null>(null)
  const [errors, setErrors] = useState<ErrorData | null>(null)
  const [missingDealers, setMissingDealers] = useState<MissingRef[]>([])
  const [missingModifications, setMissingModifications] = useState<MissingRef[]>([])
  const [missingModels, setMissingModels] = useState<MissingRef[]>([])
  const [queues, setQueues] = useState<QueueData[]>([])
  const [indices, setIndices] = useState<IndexInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [page, setPage] = useState(1)
  const [showIndices, setShowIndices] = useState(false)
  const pageSize = 20

  const getIndexPattern = useCallback((date: string, entity: EntityType): string => {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const dateStr = `${year}${month}${day}`

    if (entity === 'all') {
      return `feed-entity-*-${dateStr}`
    }
    const entityMap: Record<string, string> = {
      cars: 'car',
      modifications: 'modification',
      models: 'model',
      generations: 'generation',
    }
    return `feed-entity-${entityMap[entity]}-${dateStr}`
  }, [])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const indexPattern = getIndexPattern(selectedDate, selectedEntity)

      const [statsRes, errorsRes, dealersRes, modsRes, modelsRes, queuesRes, indicesRes] = await Promise.all([
        fetch(`${MONITOR_API_URL}/stats?index=${indexPattern}`).catch(() => null),
        fetch(`${MONITOR_API_URL}/errors?index=${indexPattern}`).catch(() => null),
        fetch(`${MONITOR_API_URL}/missing-refs/MISSING_DEALER?index=${indexPattern}`).catch(() => null),
        fetch(`${MONITOR_API_URL}/missing-refs/MISSING_MODIFICATION?index=${indexPattern}`).catch(() => null),
        fetch(`${MONITOR_API_URL}/missing-refs/MISSING_MODEL?index=${indexPattern}`).catch(() => null),
        fetch(`${MONITOR_API_URL}/queues`).catch(() => null),
        fetch(`${MONITOR_API_URL}/indices`).catch(() => null),
      ])

      const parseJson = async (res: Response | null) => {
        if (!res || !res.ok) return null
        try {
          return await res.json()
        } catch {
          return null
        }
      }

      const [statsData, errorsData, dealersData, modsData, modelsData, queuesData, indicesData] = await Promise.all([
        parseJson(statsRes),
        parseJson(errorsRes),
        parseJson(dealersRes),
        parseJson(modsRes),
        parseJson(modelsRes),
        parseJson(queuesRes),
        parseJson(indicesRes),
      ])

      setStats(statsData || { total: 0, by_status: [], by_index: [] })
      setErrors(errorsData || { by_error_type: [] })
      setMissingDealers(dealersData?.unique_refs || [])
      setMissingModifications(modsData?.unique_refs || [])
      setMissingModels(modelsData?.unique_refs || [])
      setQueues(queuesData?.queues || [])
      setIndices(indicesData?.indices || [])

      if (!statsData || statsData.total === 0) {
        setError(`No data found for ${selectedDate}. Try selecting a different date.`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data')
      setStats({ total: 0, by_status: [], by_index: [] })
      setErrors({ by_error_type: [] })
      setMissingDealers([])
      setMissingModifications([])
      setMissingModels([])
      setQueues([])
      setIndices([])
    } finally {
      setLoading(false)
    }
  }, [selectedDate, selectedEntity, getIndexPattern])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchData, 10000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh, fetchData])

  const handleQuickDate = (type: string) => {
    setQuickDate(type)
    const now = new Date()
    let date: Date
    switch (type) {
      case 'today':
        date = now
        break
      case 'yesterday':
        date = new Date(now.setDate(now.getDate() - 1))
        break
      default:
        date = now
    }
    setSelectedDate(date.toISOString().split('T')[0])
    setPage(1)
  }

  const handleRetry = async (action: string, targetId?: string) => {
    // Build confirmation message
    let title = 'Retry Entities?'
    let text = ''
    switch (action) {
      case 'RETRY_NOT_PROCESSED':
        title = 'Retry Pending Entities?'
        text = `This will queue ${notProcessedCount.toLocaleString()} pending entities for retry processing.`
        break
      case 'RETRY_ALL_FAILED':
        title = 'Retry Failed Entities?'
        text = `This will queue ${failedCount.toLocaleString()} failed entities for retry processing.`
        break
      default:
        text = targetId
          ? `This will retry entities with reference ID: ${targetId}`
          : 'This will perform the retry action.'
    }

    const result = await Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#27272a',
      confirmButtonText: 'Yes, retry',
      background: '#0f0f11',
      color: '#fafafa',
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      const indexPattern = getIndexPattern(selectedDate, selectedEntity)
      const response = await fetch(`${MONITOR_API_URL}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, target_id: targetId, index_pattern: indexPattern }),
      })
      const apiResult = await response.json()
      if (apiResult.success) {
        await Swal.fire({
          title: 'Queued!',
          text: `${apiResult.queued || 'Entities'} queued for retry.`,
          icon: 'success',
          background: '#0f0f11',
          color: '#fafafa',
          confirmButtonColor: '#3b82f6',
        })
      }
      setTimeout(fetchData, 1000)
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err instanceof Error ? err.message : 'Retry failed',
        icon: 'error',
        background: '#0f0f11',
        color: '#fafafa',
      })
    }
  }

  const handleMarkResolved = async (errorType: string, refId?: string) => {
    const title = 'Mark as Resolved?'
    const text = refId
      ? `This will mark all entities with ${errorType.replace(/_/g, ' ')} (ref: ${refId}) as resolved.`
      : `This will mark ALL ${errorType.replace(/_/g, ' ')} errors as resolved.`

    const result = await Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#22c55e',
      cancelButtonColor: '#27272a',
      confirmButtonText: 'Yes, mark resolved',
      background: '#0f0f11',
      color: '#fafafa',
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      const indexPattern = getIndexPattern(selectedDate, selectedEntity)
      const response = await fetch(`${MONITOR_API_URL}/mark-resolved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ error_type: errorType, ref_id: refId, index_pattern: indexPattern }),
      })
      const apiResult = await response.json()
      if (apiResult.success) {
        await Swal.fire({
          title: 'Marked!',
          text: `${apiResult.updated || 'Entities'} marked as resolved.`,
          icon: 'success',
          background: '#0f0f11',
          color: '#fafafa',
          confirmButtonColor: '#22c55e',
        })
      }
      setTimeout(fetchData, 1000)
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err instanceof Error ? err.message : 'Mark resolved failed',
        icon: 'error',
        background: '#0f0f11',
        color: '#fafafa',
      })
    }
  }

  const handleDbCheck = async (action: string, entityIds?: string[]) => {
    // Different messages based on action
    let title = 'Verify in Database?'
    let text = ''
    const uncheckedCount = getDbCheckCount('not_checked')

    if (action === 'DB_CHECK_PUBLISHED') {
      text = `Queue ${uncheckedCount.toLocaleString()} published & unchecked entities for verification.`
    } else if (action === 'DB_CHECK_NOT_CHECKED') {
      text = `Queue ALL unchecked entities (${uncheckedCount.toLocaleString()}) for verification.`
    } else if (action === 'DB_CHECK_ALL') {
      text = `Force re-verify ALL ${totalCount.toLocaleString()} entities (ignores existing status).`
      title = 'Force Re-Verify All?'
    } else if (action === 'DB_CHECK_SPECIFIC' && entityIds) {
      text = `Verify ${entityIds.length} specific entit${entityIds.length === 1 ? 'y' : 'ies'}.`
    }

    const result = await Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#8b5cf6',
      cancelButtonColor: '#27272a',
      confirmButtonText: 'Yes, verify',
      background: '#0f0f11',
      color: '#fafafa',
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      const indexPattern = getIndexPattern(selectedDate, selectedEntity)
      const response = await fetch(`${MONITOR_API_URL}/db-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          index_pattern: indexPattern,
          entity_type: selectedEntity === 'all' ? 'cars' : selectedEntity,
          entity_ids: entityIds,
        }),
      })
      const apiResult = await response.json()
      if (apiResult.success) {
        await Swal.fire({
          title: 'Queued!',
          text:
            apiResult.queued > 0
              ? `${apiResult.queued} entities queued in ${apiResult.batches} batches.`
              : apiResult.message,
          icon: 'success',
          background: '#0f0f11',
          color: '#fafafa',
          confirmButtonColor: '#8b5cf6',
        })
      } else {
        Swal.fire({
          title: 'Error',
          text: apiResult.error || 'DB Check failed',
          icon: 'error',
          background: '#0f0f11',
          color: '#fafafa',
        })
      }
      setTimeout(fetchData, 2000)
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err instanceof Error ? err.message : 'DB Check failed',
        icon: 'error',
        background: '#0f0f11',
        color: '#fafafa',
      })
    }
  }

  const getStatusCount = (status: string): number => {
    if (!stats || !stats.by_status) return 0
    const found = stats.by_status.find((s) => s.status === status)
    return found?.count || 0
  }

  const getErrorCount = (type: string): number => {
    if (!errors || !errors.by_error_type) return 0
    const found = errors.by_error_type.find((e) => e.error_type === type)
    return found?.count || 0
  }

  const getMatchingIndices = (): IndexInfo[] => {
    const d = new Date(selectedDate)
    const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(
      2,
      '0',
    )}`
    return indices.filter((idx) => idx.index.includes(dateStr))
  }

  const getTableData = () => {
    switch (activeFilter) {
      case 'MISSING_DEALER':
        return missingDealers.map((d) => ({ type: 'dealer', ...d }))
      case 'MISSING_MODIFICATION':
        return missingModifications.map((m) => ({ type: 'modification', ...m }))
      case 'MISSING_MODEL':
        return missingModels.map((m) => ({ type: 'model', ...m }))
      default:
        return []
    }
  }

  const tableData = getTableData()
  const totalPages = Math.ceil(tableData.length / pageSize)
  const paginatedData = tableData.slice((page - 1) * pageSize, page * pageSize)

  const publishedCount = getStatusCount('published')
  const failedCount = getStatusCount('failed')
  const notProcessedCount = getStatusCount('not_processed')
  const totalCount = stats?.total || 0
  const matchingIndices = getMatchingIndices()

  // DB Check status counts
  const getDbCheckCount = (status: string): number => {
    if (!stats || !stats.by_db_check_status) return 0
    const found = stats.by_db_check_status.find((s: { status: string; count: number }) => s.status === status)
    return found?.count || 0
  }
  const dbVerifiedCount = getDbCheckCount('verified')
  const dbMissingCount = getDbCheckCount('missing')
  const dbMismatchCount = getDbCheckCount('mismatch')
  const dbNotCheckedCount = getDbCheckCount('not_checked')

  // Calculate percentages for metrics
  const publishedPct = totalCount > 0 ? ((publishedCount / totalCount) * 100).toFixed(1) : '0'
  const failedPct = totalCount > 0 ? ((failedCount / totalCount) * 100).toFixed(1) : '0'
  const pendingPct = totalCount > 0 ? ((notProcessedCount / totalCount) * 100).toFixed(1) : '0'

  const getErrorIcon = (type: string) => {
    switch (type) {
      case 'MISSING_DEALER':
        return <Users size={14} />
      case 'MISSING_MODIFICATION':
        return <Wrench size={14} />
      case 'MISSING_MODEL':
        return <Car size={14} />
      default:
        return <AlertTriangle size={14} />
    }
  }

  return (
    <div className={styles.page}>
      {/* Compact Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.titleGroup}>
            <div className={styles.logoIcon}>
              <Layers size={18} />
            </div>
            <h1 className={styles.title}>Feed Monitor</h1>
          </div>

          <div className={styles.headerControls}>
            {/* Date Picker */}
            <div className={styles.dateGroup}>
              <Calendar size={14} className={styles.inputIcon} />
              <input
                type="date"
                className={styles.dateInput}
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value)
                  setQuickDate('')
                  setPage(1)
                }}
              />
              <div className={styles.quickDates}>
                <button
                  className={`${styles.quickBtn} ${quickDate === 'today' ? styles.active : ''}`}
                  onClick={() => handleQuickDate('today')}>
                  Today
                </button>
                <button
                  className={`${styles.quickBtn} ${quickDate === 'yesterday' ? styles.active : ''}`}
                  onClick={() => handleQuickDate('yesterday')}>
                  Yesterday
                </button>
              </div>
            </div>

            {/* Entity Selector */}
            <div className={styles.selectGroup}>
              <select
                className={styles.entitySelect}
                value={selectedEntity}
                onChange={(e) => {
                  setSelectedEntity(e.target.value as EntityType)
                  setPage(1)
                }}>
                {ENTITY_TYPES.map((et) => (
                  <option key={et.value} value={et.value}>
                    {et.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className={styles.selectIcon} />
            </div>
          </div>
        </div>

        <div className={styles.headerRight}>
          <label className={styles.autoRefresh}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            <span>Auto</span>
          </label>
          <button className={styles.refreshBtn} onClick={fetchData} disabled={loading}>
            <RefreshCw size={14} className={loading ? styles.spinning : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Metrics Strip */}
      <div className={styles.metricsStrip}>
        <div className={`${styles.metric} ${styles.metricTotal}`}>
          <div className={styles.metricIcon}>
            <Database size={16} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricValue}>{totalCount.toLocaleString()}</span>
            <span className={styles.metricLabel}>Total</span>
          </div>
        </div>

        <div className={styles.metricDivider} />

        <div className={`${styles.metric} ${styles.metricSuccess}`}>
          <div className={styles.metricIcon}>
            <CheckCircle2 size={16} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricValue}>{publishedCount.toLocaleString()}</span>
            <span className={styles.metricLabel}>Published ({publishedPct}%)</span>
          </div>
          <div className={styles.metricBar}>
            <div className={styles.metricProgress} style={{ width: `${publishedPct}%` }} />
          </div>
        </div>

        <div className={`${styles.metric} ${styles.metricError}`}>
          <div className={styles.metricIcon}>
            <XCircle size={16} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricValue}>{failedCount.toLocaleString()}</span>
            <span className={styles.metricLabel}>Failed ({failedPct}%)</span>
          </div>
          <div className={styles.metricBar}>
            <div className={styles.metricProgress} style={{ width: `${failedPct}%` }} />
          </div>
        </div>

        <div className={`${styles.metric} ${styles.metricWarning}`}>
          <div className={styles.metricIcon}>
            <Clock size={16} />
          </div>
          <div className={styles.metricContent}>
            <span className={styles.metricValue}>{notProcessedCount.toLocaleString()}</span>
            <span className={styles.metricLabel}>Pending ({pendingPct}%)</span>
          </div>
          <div className={styles.metricBar}>
            <div className={styles.metricProgress} style={{ width: `${pendingPct}%` }} />
          </div>
        </div>

        <div className={styles.metricDivider} />

        {/* DB Check Stats - Separate Section */}
        {dbVerifiedCount > 0 && (
          <div className={`${styles.metric} ${styles.metricSuccess}`}>
            <div className={styles.metricIcon}>
              <CheckCircle2 size={16} />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricValue}>{dbVerifiedCount.toLocaleString()}</span>
              <span className={styles.metricLabel}>DB Verified</span>
            </div>
          </div>
        )}

        {dbMissingCount > 0 && (
          <div className={`${styles.metric} ${styles.metricWarning}`}>
            <div className={styles.metricIcon}>
              <AlertTriangle size={16} />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricValue}>{dbMissingCount.toLocaleString()}</span>
              <span className={styles.metricLabel}>DB Missing</span>
            </div>
          </div>
        )}

        {dbMismatchCount > 0 && (
          <div className={`${styles.metric} ${styles.metricError}`}>
            <div className={styles.metricIcon}>
              <XCircle size={16} />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricValue}>{dbMismatchCount.toLocaleString()}</span>
              <span className={styles.metricLabel}>DB Mismatch</span>
            </div>
          </div>
        )}

        {dbNotCheckedCount > 0 && (
          <div className={`${styles.metric} ${styles.metricInfo}`}>
            <div className={styles.metricIcon}>
              <Clock size={16} />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricValue}>{dbNotCheckedCount.toLocaleString()}</span>
              <span className={styles.metricLabel}>Not Checked</span>
            </div>
          </div>
        )}
      </div>

      {/* Indices Bar (Collapsible) */}
      {matchingIndices.length > 0 && (
        <div className={styles.indicesBar}>
          <button className={styles.indicesToggle} onClick={() => setShowIndices(!showIndices)}>
            <Database size={12} />
            <span>{matchingIndices.length} indices</span>
            <ChevronDown size={12} className={showIndices ? styles.rotated : ''} />
          </button>
          {showIndices && (
            <div className={styles.indicesList}>
              {matchingIndices.map((idx) => (
                <span
                  key={idx.index}
                  className={`${styles.indexPill} ${
                    selectedEntity !== 'all' && idx.index.includes(selectedEntity.slice(0, -1)) ? styles.active : ''
                  }`}>
                  {idx.index.replace('feed-entity-', '')}
                  <span className={styles.indexCount}>{idx.docs_count.toLocaleString()}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className={styles.tabBar}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeFilter === 'all' ? styles.active : ''}`}
            onClick={() => {
              setActiveFilter('all')
              setPage(1)
            }}>
            <AlertTriangle size={14} />
            <span>All Errors</span>
            <span className={styles.tabBadge}>{failedCount}</span>
          </button>
          <button
            className={`${styles.tab} ${activeFilter === 'MISSING_DEALER' ? styles.active : ''}`}
            onClick={() => {
              setActiveFilter('MISSING_DEALER')
              setPage(1)
            }}>
            <Users size={14} />
            <span>Missing Dealers</span>
            <span className={styles.tabBadge}>{missingDealers.length}</span>
          </button>
          <button
            className={`${styles.tab} ${activeFilter === 'MISSING_MODIFICATION' ? styles.active : ''}`}
            onClick={() => {
              setActiveFilter('MISSING_MODIFICATION')
              setPage(1)
            }}>
            <Wrench size={14} />
            <span>Missing Mods</span>
            <span className={styles.tabBadge}>{missingModifications.length}</span>
          </button>
          <button
            className={`${styles.tab} ${activeFilter === 'MISSING_MODEL' ? styles.active : ''}`}
            onClick={() => {
              setActiveFilter('MISSING_MODEL')
              setPage(1)
            }}>
            <Car size={14} />
            <span>Missing Models</span>
            <span className={styles.tabBadge}>{missingModels.length}</span>
          </button>
          <button
            className={`${styles.tab} ${activeFilter === 'queues' ? styles.active : ''}`}
            onClick={() => {
              setActiveFilter('queues')
              setPage(1)
            }}>
            <Layers size={14} />
            <span>Queues</span>
            <span className={styles.tabBadge}>{queues.length}</span>
          </button>
        </div>

        <div className={styles.tabActions}>
          <button
            className={`${styles.actionBtn} ${styles.warning}`}
            onClick={() => handleRetry('RETRY_NOT_PROCESSED')}>
            <RotateCcw size={14} />
            <span>Retry Pending</span>
          </button>
          <button className={`${styles.actionBtn} ${styles.danger}`} onClick={() => handleRetry('RETRY_ALL_FAILED')}>
            <RotateCcw size={14} />
            <span>Retry Failed</span>
          </button>
          <div className={styles.dropdownContainer}>
            <button
              className={`${styles.actionBtn} ${styles.primary}`}
              onClick={() => handleDbCheck('DB_CHECK_NOT_CHECKED')}>
              <Database size={14} />
              <span>Verify Unchecked</span>
            </button>
            <div className={styles.dropdownMenu}>
              <button onClick={() => handleDbCheck('DB_CHECK_NOT_CHECKED')}>
                <Clock size={14} /> Verify Unchecked Only
              </button>
              <button onClick={() => handleDbCheck('DB_CHECK_PUBLISHED')}>
                <CheckCircle2 size={14} /> Verify Published Only
              </button>
              <button onClick={() => handleDbCheck('DB_CHECK_ALL')}>
                <RotateCcw size={14} /> Force Re-Verify All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className={styles.content}>
        {activeFilter === 'all' && (
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <div className={styles.tableTitle}>
                <AlertTriangle size={16} />
                <span>Error Breakdown</span>
              </div>
              <span className={styles.tableCount}>{failedCount.toLocaleString()} failed entities</span>
            </div>
            {errors && Array.isArray(errors.by_error_type) && errors.by_error_type.length > 0 ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Error Type</th>
                    <th className={styles.alignRight}>Count</th>
                    <th className={styles.alignRight}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.by_error_type.map((err) => (
                    <tr key={err.error_type}>
                      <td>
                        <div className={styles.errorType}>
                          {getErrorIcon(err.error_type)}
                          <span
                            className={`${styles.errorBadge} ${
                              styles[err.error_type.toLowerCase().replace(/_/g, '')]
                            }`}>
                            {err.error_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </td>
                      <td className={styles.alignRight}>
                        <span className={styles.countValue}>{err.count.toLocaleString()}</span>
                      </td>
                      <td className={styles.alignRight}>
                        <button className={styles.resolveBtn} onClick={() => handleMarkResolved(err.error_type)}>
                          <CheckCircle2 size={14} />
                          <span>Resolve All</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.emptyState}>
                <CheckCircle2 size={32} />
                <p>No errors found</p>
              </div>
            )}
          </div>
        )}

        {(activeFilter === 'MISSING_DEALER' ||
          activeFilter === 'MISSING_MODIFICATION' ||
          activeFilter === 'MISSING_MODEL') && (
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <div className={styles.tableTitle}>
                {getErrorIcon(activeFilter)}
                <span>
                  {activeFilter === 'MISSING_DEALER' && 'Missing Dealers'}
                  {activeFilter === 'MISSING_MODIFICATION' && 'Missing Modifications'}
                  {activeFilter === 'MISSING_MODEL' && 'Missing Models'}
                </span>
              </div>
              <span className={styles.tableCount}>
                {tableData.length} unique IDs / {getErrorCount(activeFilter)} affected
              </span>
            </div>
            {paginatedData.length > 0 ? (
              <>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Reference ID</th>
                      <th className={styles.alignRight}>Affected Entities</th>
                      <th className={styles.alignRight}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((item) => (
                      <tr key={item.ref_id}>
                        <td>
                          <code className={styles.codeCell}>{item.ref_id}</code>
                        </td>
                        <td className={styles.alignRight}>
                          <span className={styles.countValue}>{item.count.toLocaleString()}</span>
                        </td>
                        <td className={styles.alignRight}>
                          <div className={styles.rowActions}>
                            <button
                              className={styles.resolveBtn}
                              onClick={() => handleMarkResolved(activeFilter, item.ref_id)}>
                              <CheckCircle2 size={14} />
                              <span>Resolve</span>
                            </button>
                            <button
                              className={styles.retryBtn}
                              onClick={async () => {
                                // First mark as resolved, then retry with correct action name
                                const indexPattern = getIndexPattern(selectedDate, selectedEntity)
                                await fetch(`${MONITOR_API_URL}/mark-resolved`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    error_type: activeFilter,
                                    ref_id: item.ref_id,
                                    index_pattern: indexPattern,
                                  }),
                                })
                                handleRetry(`FIX_${activeFilter}`, item.ref_id)
                              }}>
                              <RotateCcw size={14} />
                              <span>Retry</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div className={styles.pagination}>
                    <span className={styles.pageInfo}>
                      {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, tableData.length)} of {tableData.length}
                    </span>
                    <div className={styles.pageButtons}>
                      <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(page - 1)}>
                        Prev
                      </button>
                      <button
                        className={styles.pageBtn}
                        disabled={page === totalPages}
                        onClick={() => setPage(page + 1)}>
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <CheckCircle2 size={32} />
                <p>No missing references found</p>
              </div>
            )}
          </div>
        )}

        {activeFilter === 'queues' && (
          <div className={styles.queueGrid}>
            {queues.length > 0 ? (
              queues.map((queue) => (
                <div key={queue.name} className={styles.queueCard}>
                  <div className={styles.queueHeader}>
                    <Layers size={14} />
                    <span className={styles.queueName}>{queue.name}</span>
                  </div>
                  <div className={styles.queueStats}>
                    <div className={styles.queueStat}>
                      <span className={styles.queueValue}>{queue.messages.toLocaleString()}</span>
                      <span className={styles.queueLabel}>messages</span>
                    </div>
                    <div className={styles.queueStat}>
                      <span className={styles.queueValue}>{queue.consumers}</span>
                      <span className={styles.queueLabel}>consumers</span>
                    </div>
                  </div>
                  <div className={styles.queueStatus}>
                    <span className={`${styles.statusDot} ${queue.consumers > 0 ? styles.online : styles.offline}`} />
                    <span>{queue.consumers > 0 ? 'Active' : 'Idle'}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <Layers size={32} />
                <p>No queues found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FeedMonitor
